'use client'

/**
 * 病害识别页与结果页之间传递压缩后图片所使用的 sessionStorage key。
 * 图片本身塞不进 URL query，因此走 sessionStorage：上传页压缩后写入，
 * 结果页读回并拼回 data URL 展示、以及原样发给 /api/disease-detection。
 */
export const DISEASE_DETECTION_IMAGE_STORAGE_KEY = 'disease_image'
export const DISEASE_DETECTION_MIME_STORAGE_KEY = 'disease_mime'

export interface CompressedImageResult {
  /** 不带 `data:` 前缀的纯 base64 内容 */
  base64: string
  mimeType: string
}

export interface CompressImageOptions {
  /** 长边上限（像素），默认 1600 */
  maxDimension?: number
  /** JPEG 压缩质量 0-1，默认 0.85 */
  quality?: number
}

const DEFAULT_MAX_DIMENSION = 1600
const DEFAULT_QUALITY = 0.85
const OUTPUT_MIME_TYPE = 'image/jpeg'

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('图片加载失败，请更换图片后重试'))
    image.src = src
  })
}

/**
 * 客户端等比压缩：长边不超过 maxDimension，输出 JPEG，quality 默认 0.85。
 * 手机原图转 base64 常常几 MB，而 sessionStorage 通常只有 5MB 上限，
 * 不压缩会直接写失败——这一步不能省。
 */
export async function compressImageFile(
  file: File,
  options: CompressImageOptions = {}
): Promise<CompressedImageResult> {
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION
  const quality = options.quality ?? DEFAULT_QUALITY

  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await loadImageElement(objectUrl)
    const sourceWidth = image.naturalWidth || image.width
    const sourceHeight = image.naturalHeight || image.height
    const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))
    const targetWidth = Math.max(1, Math.round(sourceWidth * scale))
    const targetHeight = Math.max(1, Math.round(sourceHeight * scale))

    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('当前浏览器不支持图片压缩')
    }
    context.drawImage(image, 0, 0, targetWidth, targetHeight)

    const dataUrl = canvas.toDataURL(OUTPUT_MIME_TYPE, quality)
    const commaIndex = dataUrl.indexOf(',')
    const base64 = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl
    if (!base64) {
      throw new Error('图片压缩失败')
    }

    return { base64, mimeType: OUTPUT_MIME_TYPE }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
